/**
 * ProviderPortalShell - Estrutura visual compartilhada do aplicativo do prestador.
 *
 * RELEASE: 2026-10-12 v1.0.0 - Moldura premium mobile-first, cabeçalho e navegação em cinco áreas.
 */
import type { LucideIcon } from 'lucide-react';
import { CalendarDays, ClipboardList, Home, Package, UserRound } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { ProviderSystemBrand } from './ProviderSystemBrand';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DataRefreshButton } from '@/components/common/DataRefreshButton';

export type ProviderPortalTab = 'home' | 'orders' | 'agenda' | 'stock' | 'profile';

const NAV_ITEMS: { value: ProviderPortalTab; label: string; icon: LucideIcon }[] = [
  { value: 'home', label: 'Início', icon: Home },
  { value: 'orders', label: 'OS', icon: ClipboardList },
  { value: 'agenda', label: 'Agenda', icon: CalendarDays },
  { value: 'stock', label: 'Estoque', icon: Package },
  { value: 'profile', label: 'Perfil', icon: UserRound },
];

/** Título exibido no header para cada área do aplicativo. */
const TITLES: Record<ProviderPortalTab, string> = {
  home: 'Início', orders: 'Ordens de serviço', agenda: 'Agenda', stock: 'Meu estoque', profile: 'Perfil',
};

interface ProviderPortalShellProps {
  children: ReactNode;
  tab?: ProviderPortalTab;
  onTabChange?: (tab: ProviderPortalTab) => void;
  tenant?: { name: string; logo_url: string | null };
  hideNavigation?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
}

export function ProviderPortalShell({ children, tab, onTabChange, tenant, hideNavigation, onRefresh, refreshing = false }: ProviderPortalShellProps) {
  const { tenantSlug = '' } = useParams();
  // Atalho na tela inicial aponta para /prestador/:slug/ enquanto o portal está montado.
  // Avisos globais ficam acima da navegação somente enquanto o portal está montado.
  useEffect(() => {
    document.body.classList.add('provider-portal-open');
    return () => document.body.classList.remove('provider-portal-open');
  }, []);
  return (
    // Moldura fixa do tamanho da tela: header e menu ficam imóveis; somente o <main> rola,
    // com overscroll contido para evitar o efeito "elástico" que mostrava faixas brancas.
    <div className="provider-app h-[100dvh] overflow-hidden bg-background text-foreground">
      <div className="mx-auto flex h-full max-w-2xl flex-col bg-background sm:border-x">
        {tenant && !hideNavigation && (
          <header className="z-30 shrink-0 border-b border-sidebar-accent bg-sidebar px-4 pb-3 pt-[calc(.75rem+env(safe-area-inset-top))] text-sidebar-foreground sm:px-6">
            <div className="flex items-center gap-2">
            <ProviderSystemBrand compact />
            {/* Nome do menu centralizado no header: libera espaço vertical dentro do conteúdo. */}
            <p className="min-w-0 flex-1 truncate text-center text-base font-bold">{TITLES[tab ?? 'home']}</p>
            {onRefresh && <DataRefreshButton onRefresh={onRefresh} refreshing={refreshing} className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground" />}
            <Button variant="ghost" size="icon" onClick={() => onTabChange?.('profile')} className="h-10 w-10 shrink-0 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground" aria-label="Abrir perfil" title="Perfil"><UserRound className="h-5 w-5" /></Button>
            </div>
          </header>
        )}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto overscroll-contain px-4 py-5 sm:px-6">{children}</main>
        {!hideNavigation && tab && onTabChange && (
          <nav aria-label="Navegação principal" className="z-40 grid h-[calc(5rem+env(safe-area-inset-bottom))] shrink-0 grid-cols-5 border-t border-border/60 bg-card px-1 pb-[env(safe-area-inset-bottom)] shadow-[var(--portal-nav-shadow)]">
            {NAV_ITEMS.map(({ value, label, icon: Icon }) => (
              <Button key={value} variant="ghost" onClick={() => onTabChange(value)}
                aria-current={tab === value ? 'page' : undefined}
                className={cn('h-full min-w-0 flex-col gap-1 rounded-none px-0 text-xs font-semibold transition-colors active:bg-accent', tab === value ? 'text-primary' : 'text-muted-foreground')}>
                <span className={cn('flex h-8 w-12 items-center justify-center rounded-full transition-colors', tab === value && 'bg-secondary')}><Icon className="h-5 w-5" strokeWidth={tab === value ? 2.3 : 1.8} /></span>
                {label}
              </Button>
            ))}
          </nav>
        )}
      </div>
    </div>
  );
}
